/**
 * FreightProgressCard — manager cockpit input for real pallet numbers.
 *
 * Four numbers, all clamped 0..999 with +/- steppers:
 *   • Received today      — auto-filled from the weekly truck plan on
 *                           first open so the SM just confirms.
 *   • Cleared today       — pallets the crew worked out to the floor.
 *   • In backroom now     — the big number: what's sitting back there
 *                           right now. Drives the AI Coach freight_read.
 *   • Target by close     — the SM's finish line for the day.
 *
 * Manager-only. Non-managers see the card in read-only mode (numbers
 * are visible, the +/- controls disappear) so any WowCrew member can
 * still glance at the state without editing.
 *
 * Writes go to PATCH /api/freight/{date}. The card debounces its own
 * saves so tapping +/- five times in a row = one PATCH.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, Minus, Sparkle, Trophy, UserPlus } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny inline stepper — big taps, minimal chrome. Values are clamped
// to the same 0..999 range the backend enforces.
function Stepper({ value, onChange, testid, disabled, tint = "slate" }) {
  const bump = (d) => onChange(Math.max(0, Math.min(999, (value || 0) + d)));
  const tintClass = tint === "emerald"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tint === "amber"
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : tint === "indigo"
        ? "bg-indigo-50 border-indigo-200 text-indigo-900"
        : "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div
      className={`inline-flex items-center rounded-full border ${tintClass} ${disabled ? "opacity-50" : ""}`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => bump(-1)}
        disabled={disabled}
        className="w-8 h-8 inline-flex items-center justify-center hover:brightness-95 disabled:cursor-not-allowed"
        aria-label="Decrease"
        data-testid={`${testid}-minus`}
      >
        <Minus weight="bold" className="w-3.5 h-3.5" />
      </button>
      <span className="min-w-[2.25rem] px-1 text-center font-black tabular-nums text-base" data-testid={`${testid}-value`}>
        {value ?? 0}
      </span>
      <button
        type="button"
        onClick={() => bump(1)}
        disabled={disabled}
        className="w-8 h-8 inline-flex items-center justify-center hover:brightness-95 disabled:cursor-not-allowed"
        aria-label="Increase"
        data-testid={`${testid}-plus`}
      >
        <Plus weight="bold" className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function FreightProgressCard({ storeId, date, user }) {
  const isManager = !!(user?.is_manager || user?.is_admin || user?.is_dm);
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Debounce writes so a burst of +/- taps only fires ONE PATCH.
  const patchTimer = useRef(null);
  const dirtyRef = useRef(null);
  // Manager "give freight credit" flow (2026-06-09) — credit anyone who
  // worked the truck but wasn't assigned / never logged.
  const [creditOpen, setCreditOpen] = useState(false);
  const [crewList, setCrewList] = useState([]);
  const [creditCrewId, setCreditCrewId] = useState("");
  const [creditPallets, setCreditPallets] = useState(1);
  const [creditNote, setCreditNote] = useState("");
  const [creditSaving, setCreditSaving] = useState(false);

  const openCredit = async () => {
    setCreditOpen(true);
    setCreditCrewId(""); setCreditPallets(1); setCreditNote("");
    try {
      const r = await axios.get(`${API}/crew`, { params: { store_id: storeId, active_only: true } });
      setCrewList(Array.isArray(r.data) ? r.data : []);
    } catch { setCrewList([]); }
  };

  const submitCredit = async () => {
    if (!creditCrewId || creditPallets < 1) return;
    setCreditSaving(true);
    try {
      await axios.post(`${API}/freight/${date}/credit`, {
        actor_id: user.crew_id,
        crew_id: creditCrewId,
        pallets: Number(creditPallets),
        note: creditNote.trim() || undefined,
      }, { params: { store_id: storeId } });
      const nm = (crewList.find((c) => c.id === creditCrewId)?.first_name) || "Crew";
      toast.success(`Credited ${creditPallets} pallet${creditPallets > 1 ? "s" : ""} to ${nm}`);
      setCreditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't give credit");
    } finally {
      setCreditSaving(false);
    }
  };

  const load = async () => {
    if (!user?.crew_id || !date || !storeId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/freight/${date}`, { params: { store_id: storeId, actor_id: user.crew_id } });
      setData(r.data || null);
    } catch (e) {
      // Silent — card just stays empty if it can't load.
    } finally {
      setLoading(false);
    }
    loadBoard();
  };

  // Per-crew pallets-cleared leaderboard — a friendly race to clear the
  // truck. Ranked highest-first; only shows once at least one crew is on
  // freight today. Polls every 30s so a manager watching the cockpit
  // sees the standings climb live as crew log from their task menu.
  const loadBoard = async () => {
    if (!date || !storeId) return;
    try {
      const r = await axios.get(`${API}/freight/${date}/assignments`, {
        params: { store_id: storeId, actor_id: user?.crew_id || "" },
      });
      const rows = (r.data?.assignments || [])
        .map((a) => ({
          crew_id: a.crew_id,
          crew_name: a.crew_name || "Crew",
          cleared: Number(a.cleared_pallets || 0),
        }))
        .sort((x, y) => y.cleared - x.cleared);
      setBoard(rows);
    } catch (e) {
      setBoard([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [storeId, date, user?.crew_id]);

  // Live-refresh just the leaderboard every 30s (cheap, slim payload).
  useEffect(() => {
    if (!date || !storeId) return;
    const t = setInterval(loadBoard, 30_000);
    return () => clearInterval(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [storeId, date]);

  const commitNow = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/freight/${date}?store_id=${encodeURIComponent(storeId)}`, {
        actor_id: user.crew_id,
        ...patch,
      });
      setData(r.data || null);
      dirtyRef.current = null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save freight numbers.");
    } finally {
      setSaving(false);
    }
  };

  const schedulePatch = (field, value) => {
    // Local optimistic update so the stepper feels instant.
    setData((prev) => ({ ...(prev || {}), [field]: value }));
    dirtyRef.current = { ...(dirtyRef.current || {}), [field]: value };
    if (patchTimer.current) clearTimeout(patchTimer.current);
    patchTimer.current = setTimeout(() => {
      if (dirtyRef.current) commitNow(dirtyRef.current);
    }, 700);
  };

  const inBackroom = data?.pallets_in_backroom ?? 0;
  const cleared = data?.pallets_cleared ?? 0;
  const received = data?.pallets_received ?? 0;
  const target = data?.target_by_close ?? 0;
  // Rough progress vs target — clamps to 0..100 so a wildly high
  // target doesn't skew the bar. Only shown when target > 0.
  const pctToTarget = useMemo(() => {
    if (!target) return 0;
    return Math.max(0, Math.min(100, Math.round((cleared / target) * 100)));
  }, [cleared, target]);

  const heavy = inBackroom >= 4;
  const bigTint = heavy ? "bg-amber-500" : inBackroom > 0 ? "bg-sky-500" : "bg-emerald-500";

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
      data-testid="freight-progress-card"
    >
      <header className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-slate-900 text-white inline-flex items-center justify-center">
          <Package weight="fill" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Freight Progress</p>
          <p className="text-[13px] font-black text-slate-900">Backroom · Today</p>
        </div>
        {data?.updated_by_name ? (
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">
            {saving ? "Saving…" : `by ${data.updated_by_name}`}
          </span>
        ) : null}
      </header>

      {/* BIG in-backroom number */}
      <div
        className="rounded-xl px-4 py-3 mb-3 flex items-center justify-between text-white"
        style={{ background: heavy
          ? "linear-gradient(135deg, #b45309 0%, #ea580c 100%)"
          : inBackroom > 0
            ? "linear-gradient(135deg, #0369a1 0%, #0284c7 100%)"
            : "linear-gradient(135deg, #047857 0%, #059669 100%)" }}
      >
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/70">In Backroom NOW</p>
          <p className="text-4xl font-black leading-none tabular-nums" data-testid="freight-in-backroom-value">{inBackroom}</p>
          <p className="text-[10px] text-white/80 mt-1">
            {loading ? "Loading…" :
              inBackroom === 0
                ? "Clean back room — nice work"
                : heavy
                  ? "Heavy — protect close, get help"
                  : "Manageable — steady clear"}
          </p>
        </div>
        {isManager && (
          <Stepper
            value={inBackroom}
            onChange={(v) => schedulePatch("pallets_in_backroom", v)}
            testid="freight-in-backroom-stepper"
            disabled={loading}
          />
        )}
      </div>

      {/* Three secondary numbers in a row */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <NumberBlock
          label="Received"
          value={received}
          testid="freight-received"
          tint="indigo"
          isManager={isManager}
          onChange={(v) => schedulePatch("pallets_received", v)}
          disabled={loading}
          hint={data?.auto_filled_received ? "Auto-filled · confirm" : ""}
        />
        <NumberBlock
          label="Cleared"
          value={cleared}
          testid="freight-cleared"
          tint="emerald"
          isManager={isManager}
          onChange={(v) => schedulePatch("pallets_cleared", v)}
          disabled={loading}
        />
        <NumberBlock
          label="Target"
          value={target}
          testid="freight-target"
          tint="amber"
          isManager={isManager}
          onChange={(v) => schedulePatch("target_by_close", v)}
          disabled={loading}
        />
      </div>

      {/* Progress bar toward target */}
      {target > 0 && (
        <div className="mb-2" data-testid="freight-progress-bar">
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full transition-all ${bigTint}`}
              style={{ width: `${pctToTarget}%` }}
            />
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            {cleared}/{target} cleared · {pctToTarget}% to close-of-day target
          </p>
        </div>
      )}

      {/* Clear-the-Truck leaderboard — per-crew pallets cleared today */}
      {(board.length > 0 || isManager) && (
        <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-2.5" data-testid="freight-leaderboard">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Trophy weight="fill" className="w-3 h-3 text-amber-500" /> Clear-the-Truck Leaderboard
            </p>
            {isManager && (
              <button
                type="button"
                onClick={openCredit}
                className="inline-flex items-center gap-1 text-[10px] font-black text-sky-700 bg-sky-100 hover:bg-sky-200 rounded-full px-2 py-1 transition-colors"
                data-testid="freight-credit-open-btn"
              >
                <UserPlus weight="bold" className="w-3 h-3" /> Credit crew
              </button>
            )}
          </div>
          {board.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic" data-testid="freight-leaderboard-empty">
              No one on freight yet. Use “Credit crew” to give credit for work done.
            </p>
          ) : (
          <div className="space-y-1.5">
            {board.map((r, i) => {
              const top = board[0]?.cleared || 0;
              const pct = top > 0 ? Math.round((r.cleared / top) * 100) : 0;
              return (
                <div key={r.crew_id || i} className="flex items-center gap-2" data-testid={`freight-board-row-${i}`}>
                  <span className={`w-4 text-center text-[11px] font-black ${i === 0 ? "text-amber-600" : "text-slate-400"}`}>{i + 1}</span>
                  <span className="flex-1 min-w-0 truncate text-[12px] font-semibold text-slate-800">{r.crew_name}</span>
                  <div className="w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div className={`h-full transition-all ${i === 0 ? "bg-amber-500" : "bg-sky-400"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-7 text-right text-[12px] font-black tabular-nums text-slate-900" data-testid={`freight-board-cleared-${i}`}>{r.cleared}</span>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* Give-credit dialog (manager-only) */}
      {creditOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => !creditSaving && setCreditOpen(false)}
          data-testid="freight-credit-dialog"
        >
          <div
            className="w-full sm:max-w-sm bg-white rounded-2xl p-4 space-y-3 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p className="text-base font-black text-slate-900">Give freight credit</p>
              <p className="text-[11px] text-slate-500">Credits the crew member on the leaderboard for {date}, even if they weren’t assigned.</p>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wide text-slate-500">Crew member</label>
              <select
                value={creditCrewId}
                onChange={(e) => setCreditCrewId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm font-semibold text-slate-800 bg-white"
                data-testid="freight-credit-crew-select"
              >
                <option value="">Select crew…</option>
                {crewList.map((c) => (
                  <option key={c.id} value={c.id}>{c.first_name}{c.last_name ? ` ${c.last_name}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-wide text-slate-500">Pallets</label>
              <Stepper value={creditPallets} onChange={setCreditPallets} testid="freight-credit-pallets" tint="sky" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wide text-slate-500">Note (optional)</label>
              <input
                type="text"
                value={creditNote}
                onChange={(e) => setCreditNote(e.target.value)}
                placeholder="e.g. Worked freight, wasn’t assigned"
                maxLength={140}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-slate-800"
                data-testid="freight-credit-note"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setCreditOpen(false)}
                disabled={creditSaving}
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-600"
                data-testid="freight-credit-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={submitCredit}
                disabled={creditSaving || !creditCrewId}
                className="flex-1 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-50 px-3 py-2 text-sm font-black text-white"
                data-testid="freight-credit-submit"
              >{creditSaving ? "Crediting…" : "Give credit"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Coach hint — nudge SM to open the AI Assistant if they're heavy */}
      {isManager && heavy && (
        <div
          className="mt-1 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-[11px] text-slate-700 inline-flex items-center gap-1.5"
          data-testid="freight-coach-nudge"
        >
          <Sparkle weight="fill" className="w-3.5 h-3.5 text-purple-600" />
          <span>Backroom is heavy — ask the AI Assistant to build a clear-out plan.</span>
        </div>
      )}
    </section>
  );
}

function NumberBlock({ label, value, testid, tint, isManager, onChange, disabled, hint }) {
  const tintClass = tint === "emerald"
    ? "text-emerald-700"
    : tint === "amber"
      ? "text-amber-700"
      : tint === "indigo"
        ? "text-indigo-700"
        : "text-slate-700";
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-2 text-center">
      <p className={`text-[9px] font-black uppercase tracking-wider ${tintClass}`}>{label}</p>
      <p className="text-2xl font-black tabular-nums text-slate-900 leading-tight" data-testid={`${testid}-value`}>{value}</p>
      {isManager ? (
        <div className="mt-1 flex justify-center">
          <Stepper value={value} onChange={onChange} testid={`${testid}-stepper`} disabled={disabled} tint={tint} />
        </div>
      ) : null}
      {hint ? <p className="text-[9px] text-slate-500 mt-1 italic">{hint}</p> : null}
    </div>
  );
}
